/**
 * Partner Portal password policy — same rules as the Sophia LMS (SOPHIA-34868):
 * 10–64 characters with at least one uppercase letter, one lowercase letter,
 * one number, and one special character.
 *
 * Shared by the password reset page (SOPHIA-34869) and the account-claiming
 * flow (SOPHIA-34868).
 */

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 64;

export interface PasswordRule {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'min-length',
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    test: (p) => p.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'max-length',
    label: `At most ${PASSWORD_MAX_LENGTH} characters`,
    test: (p) => p.length <= PASSWORD_MAX_LENGTH,
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter',
    test: (p) => /[A-Z]/.test(p),
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter',
    test: (p) => /[a-z]/.test(p),
  },
  {
    id: 'number',
    label: 'One number',
    test: (p) => /[0-9]/.test(p),
  },
  {
    id: 'special',
    label: 'One special character',
    test: (p) => /[^A-Za-z0-9]/.test(p),
  },
];

/** Returns the rules the given password fails (empty array = valid). */
export function getFailedPasswordRules(password: string): PasswordRule[] {
  return PASSWORD_RULES.filter((rule) => !rule.test(password));
}

/** True when the password satisfies the full policy. */
export function isPasswordValid(password: string): boolean {
  return getFailedPasswordRules(password).length === 0;
}
